/** Team workspace membership (V2 additive) — rides along on get-user-info. */
export interface Organization {
  orgId: string;
  name: string;
  role: 'owner' | 'admin' | 'member';
  orgStatus: 'pending' | 'active' | 'expired';
  seats: number;
  weeklyCredits: number;
  billingPeriod?: 'weekly' | 'yearly';
  remainingCredit: number;
}

export interface User {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  remainingCredit: number;
  userPlan: 'free' | 'pro' | 'max' | 'expired';
  createdAt: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  profilePictureUrl?: string;
  abGroup?: number;
  /** Which video provider serves this user's NEW projects (get-user-info).
   *  Existing projects keep the provider stored on their own order row. */
  videoProvider?: 'longvid' | 'heygen' | 'videogen';
  priceType?: 'weekly' | 'monthly' | 'yearly';
  retentionOfferUsed?: string;
  trialFailed?: boolean;
  /** Auto-reload credits (paid): the client only uses these to decide when to
   *  NUDGE the server check — all real validation happens in v2-auto-reload. */
  autoReloadEnabled?: boolean;
  autoReloadThreshold?: number;
  /** Team workspaces the user belongs to (empty when none). */
  organizations?: Organization[];
}

export type UserPlan = 'free' | 'pro' | 'max' | 'expired';

export interface OrganizationResponse {
  org_id: string;
  name?: string;
  role?: string;
  org_status?: string;
  seats?: number;
  weekly_credits?: number;
  billing_period?: string;
  remaining_credit?: number;
}

export interface UserResponse {
  user_id: string;
  user_email?: string;
  user_name?: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  profile_picture_url?: string;
  remaining_credit?: number;
  user_plan?: string;
  created_at?: string;
  ab_group?: number;
  video_provider?: string;
  price_type?: string;
  stripe_price_type?: string;
  retention_offer_used?: string;
  trial_failed?: string;
  // DynamoDB numbers arrive as strings (json.dumps(default=str) on Decimal).
  auto_reload_enabled?: boolean;
  auto_reload_threshold?: number | string;
  organizations?: OrganizationResponse[];
}

export function parseOrganization(data: OrganizationResponse): Organization {
  return {
    orgId: data.org_id,
    name: data.name || '',
    role: (data.role as Organization['role']) || 'member',
    orgStatus: (data.org_status as Organization['orgStatus']) || 'pending',
    seats: data.seats ?? 2,
    weeklyCredits: data.weekly_credits ?? 200,
    billingPeriod: (data.billing_period as Organization['billingPeriod']) || undefined,
    remainingCredit: data.remaining_credit ?? 0,
  };
}

export function parseUser(data: UserResponse): User {
  return {
    id: data.user_id,
    name: data.user_name || data.display_name || data.user_email || '',
    email: data.user_email || '',
    avatarUrl: data.profile_picture_url,
    remainingCredit: data.remaining_credit ?? 0,
    userPlan: (data.user_plan?.toLowerCase() as User['userPlan']) || 'free',
    createdAt: data.created_at || new Date().toISOString(),
    firstName: data.first_name,
    lastName: data.last_name,
    displayName: data.display_name,
    profilePictureUrl: data.profile_picture_url,
    abGroup: data.ab_group != null ? Number(data.ab_group) : undefined,
    videoProvider: (data.video_provider as User['videoProvider']) || undefined,
    priceType: ((data.stripe_price_type || data.price_type) as User['priceType']) || undefined,
    retentionOfferUsed: data.retention_offer_used || undefined,
    trialFailed: !!data.trial_failed,
    autoReloadEnabled: data.auto_reload_enabled === true,
    autoReloadThreshold: data.auto_reload_threshold != null ? Number(data.auto_reload_threshold) : undefined,
    organizations: Array.isArray(data.organizations)
      ? data.organizations.map(parseOrganization)
      : undefined,
  };
}

